import { useState } from 'react'
import { useLocation, useSearchParams } from 'react-router-dom'
import { useAgentSession } from '../hooks/useAgentSession'
import { captionReducer, EMPTY_CAPTION } from '../lib/captions'
import { Orb } from './Orb'
import { Captions } from './Captions'
import { OrderPanel, type OrderState } from './OrderPanel'
import { MenuDrawer } from './MenuDrawer'
import { EndSessionConfirm } from './EndSessionConfirm'

function Shell({ children }: { children: React.ReactNode }): JSX.Element {
  return (
    // touch-action: manipulation kills the double-tap zoom on a surface whose
    // whole job is being tapped, without taking pinch-zoom away from someone
    // who needs to magnify the menu.
    <div
      className="flex h-dvh w-full flex-col overflow-hidden bg-[#0b0b10]"
      style={{ touchAction: 'manipulation', overscrollBehavior: 'none' }}
    >
      {children}
    </div>
  )
}

/** A stranger's phone opens this, so it says what is wrong and stops. */
function DeadEnd({ title, detail }: { title: string; detail: string }): JSX.Element {
  return (
    <Shell>
      <div className="flex flex-1 flex-col items-center justify-center px-6 text-center">
        <Orb analysers={null} />
        <h1 className="mt-8 text-[18px] font-semibold text-white">{title}</h1>
        <p className="mt-2 max-w-xs text-[14px] leading-relaxed text-white/50">{detail}</p>
      </div>
    </Shell>
  )
}

export function TableOrderPage(): JSX.Element {
  const [params] = useSearchParams()
  const { search } = useLocation()
  const propertyId = params.get('property_id')
  const table = params.get('table')

  const [caption, setCaption] = useState(EMPTY_CAPTION)
  const [order, setOrder] = useState<OrderState | null>(null)
  const [confirmingEnd, setConfirmingEnd] = useState(false)

  const session = useAgentSession({
    search,
    onMessage: (msg) => {
      if (msg.type === 'order') {
        // Mirrored wholesale. Every function call the agent makes pushes one of
        // these, including ones that change nothing, so identical repeats are
        // normal and harmless.
        setOrder({ currency: msg.currency, lines: msg.lines, total: msg.total })
      } else {
        setCaption((prev) => captionReducer(prev, msg))
      }
    },
  })

  // Without a property there is nothing to connect to and no menu to read.
  if (!propertyId) {
    return (
      <DeadEnd
        title="This code is missing its property"
        detail="Please ask a member of staff — the sticker on this table needs reprinting."
      />
    )
  }

  // Checked before the microphone is ever requested. getUserMedia refuses on an
  // insecure origin, and a raw permission failure tells a customer nothing.
  // localhost counts as secure, so development is unaffected.
  if (!window.isSecureContext) {
    return (
      <DeadEnd
        title="Voice ordering needs a secure connection"
        detail="This page was opened over an insecure link, so the phone will not allow the microphone. Please ask a member of staff."
      />
    )
  }

  if (session.phase === 'ended') {
    return (
      <DeadEnd
        title="Thanks — that's all sent"
        detail="Scan the code again if you'd like to order something else."
      />
    )
  }

  const live = session.phase === 'live'
  const reconnecting = session.phase === 'reconnecting'
  // `sphere` is the orb alone. Ops flips it without a frontend redeploy.
  const full = session.config.phone_ui === 'full'

  return (
    <Shell>
      {table && (
        <p className="shrink-0 pt-6 text-center text-[12px] uppercase tracking-widest text-white/30">
          Table {table}
        </p>
      )}

      {/* The stage. Tapping it starts the session, then interrupts the agent.
          The End button and the drawer sit outside it, so neither can be
          mistaken for an interrupt. */}
      <button
        type="button"
        onClick={live ? session.interrupt : session.start}
        disabled={session.phase === 'connecting'}
        className="flex min-h-0 flex-1 flex-col items-center justify-center overflow-y-auto px-6 text-center outline-none"
        aria-label={live ? 'Interrupt' : 'Start ordering'}
      >
        {/* Idle, listening and speaking are read from the audio itself, not
            from the session phase — the orb follows the voice. */}
        <Orb analysers={session.analysers} />

        <p className="mt-8 text-[15px] text-white/70">
          {session.phase === 'idle' && 'Tap to start'}
          {session.phase === 'connecting' && 'Connecting…'}
          {live && 'Listening — tap to interrupt'}
          {reconnecting && 'Reconnecting…'}
          {session.phase === 'failed' && (session.error ?? 'The connection dropped.')}
        </p>

        {/* The order the agent forgot. Left on screen on purpose — it is the
            customer's own list to read back from. */}
        {session.notice && (
          <p className="mt-5 max-w-xs rounded-xl bg-amber-400/10 px-4 py-3 text-[13px] leading-relaxed text-amber-200">
            {session.notice}
          </p>
        )}

        {full && (
          <>
            <Captions caption={caption} />
            <OrderPanel order={order} />
          </>
        )}
      </button>

      <div className="flex shrink-0 items-center justify-center py-5">
        {session.phase === 'failed' && (
          <button
            type="button"
            onClick={session.start}
            className="rounded-full border border-white/20 px-5 py-2 text-[14px] text-white/80"
          >
            Try again
          </button>
        )}
        {(live || reconnecting) && (
          <button
            type="button"
            onClick={() => setConfirmingEnd(true)}
            className="rounded-full border border-white/20 px-5 py-2 text-[14px] text-white/60"
          >
            End
          </button>
        )}
      </div>

      {full && <MenuDrawer propertyId={propertyId} />}

      {confirmingEnd && (
        <EndSessionConfirm
          onCancel={() => setConfirmingEnd(false)}
          onConfirm={() => {
            setConfirmingEnd(false)
            session.end()
          }}
        />
      )}
    </Shell>
  )
}
